import { GregorioNoteElement } from '../../gregorio-chant/structures/GregorioNoteElement';
import { ChantContext } from '../../gregorio-chant/ChantContext';
import { Glyph } from '../glyphs/Glyph';
import { ChantLayoutElement } from '../layout/ChantLayoutElement';
import { calculateHeightPositionFromPitch } from '../utils';
import { GlyphVisualizer } from './GlyphVisualizer';
import { VisualizerHelper } from './VisualizerHelper';
import { GregorioGlyph } from '../../gregorio-chant/structures/GregorioGlyph';
import { GregorioGlyphType } from '../../gregorio-chant/enums/GregorioGlyphType';
import { GregorioLiquescentia } from '../../gregorio-chant/enums/GregorioLiquescentia';
import { GregorioShape } from '../../gregorio-chant/enums/GregorioShape';
import { GregorioSign } from '../../gregorio-chant/enums/GregorioSign';
import { GregorioVPosition } from '../../gregorio-chant/enums/GregorioVPosition';
import { GregorioType } from '../../gregorio-chant/enums/GregorioType';
import { GregorioSpace } from '../../gregorio-chant/enums/GregorioSpace';
import { Rect } from '../../drawing';
import { GregorioElement } from '../../gregorio-chant/structures/GregorioSyllable';


function is_between_lines(pitch: number): boolean {
  return pitch % 2 == 1;
}

function is_on_a_line(pitch: number): boolean {
  return pitch % 2 == 0;
}

export class Mora extends ChantLayoutElement {
  private readonly _visualizer: GlyphVisualizer;
  private readonly _pitch: number;
  private readonly _noSpace: number;
  private readonly _specialPunctum: number;
  private readonly _punctumInclinatum: number;

  // eslint-disable-next-line max-len
  private constructor(context: ChantContext, pitch: number, noSpace: number, specialPunctum: number, punctumInclinatum: number) {
    super(context);
    this._pitch = pitch;
    this._noSpace = noSpace;
    this._specialPunctum = specialPunctum;
    this._punctumInclinatum = punctumInclinatum;
    this._visualizer = new GlyphVisualizer(this._context,
      new Glyph('AuctumMora', this._context.gabcFont, this._context.gabcGlyphSize), this._context.gabcColor);
  }

  performLayout(previousElements: ChantLayoutElement[]) {
    const noteElement = VisualizerHelper.findLastGlyphOrSpaceOrBar(previousElements);
    if (noteElement === undefined) {
      return;
    }

    let glyphraise = 0;
    switch (this._specialPunctum) {
      case 0:
        if (is_on_a_line(this._pitch)) {
          glyphraise = this._distanceConverter.lengthToPx(this._context.linepunctummorashift);
        } else {
          glyphraise = this._distanceConverter.lengthToPx(this._context.spacepunctummorashift);
        }
        break;
      case 1:
        if (is_on_a_line(this._pitch)) {
          glyphraise = this._distanceConverter.lengthToPx(this._context.lineporrectuspunctummorashift);
        } else {
          glyphraise = this._distanceConverter.lengthToPx(this._context.spaceporrectuspunctummorashift);
        }
        break;
      case 2:
        glyphraise = this._distanceConverter.lengthToPx(this._context.spaceamonepespunctummorashift);
        break;
    }

    let y = calculateHeightPositionFromPitch(this._context, this._pitch);
    if (is_on_a_line(this._pitch)) {
      if (this._specialPunctum === 1) {
        y += this._context.staffLinesInterval;
      } else {
        y -= this._context.staffLinesInterval;
      }
    }
    y -= glyphraise;

    this._visualizer.setPosition(y);

    const spacebeforesigns = this._distanceConverter.lengthToPx(this._context.spacebeforesigns);
    if (this._noSpace === 2) {
      this._visualizer.bounds.setX(noteElement.bounds.right - this._visualizer.bounds.width);
      this.bounds =
        Rect.create(this._visualizer.bounds.x, y, this._visualizer.bounds.width, this._visualizer.bounds.height);

    } else if (this._noSpace === 1) {
      this._visualizer.bounds.setX(noteElement.bounds.right + spacebeforesigns);
      this.bounds =
        Rect.create(noteElement.bounds.right, y, 0, this._visualizer.bounds.height);
      
    } else if (this._noSpace === 3) {
      // eslint-disable-next-line max-len
      this._visualizer.bounds.setX(noteElement.bounds.right - this._visualizer.bounds.width / 2 - this._context.pitchWidth / 2);
      this.bounds =
        Rect.create(this._visualizer.bounds.x, y, this._visualizer.bounds.width, this._visualizer.bounds.height);

    } else {
      this._visualizer.bounds.setX(noteElement.bounds.right + spacebeforesigns);
      this.bounds =
        Rect.create(this._visualizer.bounds.x, y, this._visualizer.bounds.width, this._visualizer.bounds.height);
    }
  }

  public createSvgFragments(): SVGElement[] {
    return [
      ...this._visualizer.createSvgFragments(),
      ...super.createSvgFragments(),
    ];
  }

  public static writePunctumMora(
    context: ChantContext,
    glyph: GregorioGlyph,
    current_note: GregorioNoteElement,
    current_note_index: number,
    fuse_to_next_note: number,
    next_glyph?: GregorioGlyph,
    next_next_glyph?: GregorioGlyph,
    next_note?: GregorioNoteElement,
    next_next_note?: GregorioNoteElement,
    previous_note?: GregorioNoteElement,
    next_element?: GregorioElement): Mora {
    /* in this if we consider that the puncta are only on the last two notes
    * (maybe it would be useful to consider it more entirely, but it would be
    * really weird...) */
    /* the variable that will be set to true if we have to shift the punctum
    * inclinatum before the last note */
    let shift_before = false;
    /* this variable will be set to 1 if we are on the note before the last
    * note of a podatus or a porrectus or a torculus resupinus */
    let special_punctum = 0;
    /* 0 if space is normal, 1 if there should be no space after a punctum */
    /* the pitch where to set the punctum */
    let pitch = current_note.pitch;
    /* a variable to know if we are on a punctum inclinatum or not */
    let punctum_inclinatum = 0;
    /* a temp variable */

    /* we go into this switch only if it is the note before the last note */
    if (next_note) {
      switch (glyph.notes.glyph_type) {
        case GregorioGlyphType.FLEXA:
        case GregorioGlyphType.TORCULUS:
        case GregorioGlyphType.TORCULUS_RESUPINUS_FLEXUS:
        case GregorioGlyphType.PORRECTUS_FLEXUS:
          if (!(glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS)) {
            shift_before = true;
          }
          if (is_between_lines(pitch) && pitch - next_note.pitch == 1) {
            special_punctum = 2;
          }
          break;
        case GregorioGlyphType.PODATUS:
          // eslint-disable-next-line max-len
          if ((current_note.shape != GregorioShape.PUNCTUM && current_note.shape != GregorioShape.QUILISMA) || (glyph.notes.liquescentia & (GregorioLiquescentia.AUCTUS_DESCENDENS | GregorioLiquescentia.AUCTUS_ASCENDENS))) {
            shift_before = true;
            /* fine tuning */
            if (next_note.pitch - current_note.pitch == 1) {
              if (is_on_a_line(current_note.pitch)) {
                special_punctum = 1;
              } else {
                pitch = current_note.pitch - 1;
              }
            }
          } else {
            /* case for f.g */
            if (next_note.pitch - current_note.pitch == 1) {
              special_punctum = 1;
            }
          }
          break;
        case GregorioGlyphType.PES_QUADRATUM:
          shift_before = true;
          if (next_note.pitch - current_note.pitch ==
            1) {
            if (is_on_a_line(current_note.pitch)) {
              special_punctum = 1;
            } else {
              pitch = current_note.pitch - 1;
            }
          }
          break;
        case GregorioGlyphType.PORRECTUS:
        case GregorioGlyphType.TORCULUS_RESUPINUS:
          /* this case is only for the note before the previous note */
          // eslint-disable-next-line max-len
          if ((next_note.pitch - current_note.pitch == -1 || next_note.pitch - current_note.pitch == 1) && !(next_next_note))
            special_punctum = 1;
          break;
        default:
          break;
      }
    }
    /* we enter here in any case */
    switch (glyph.notes.glyph_type) {
      case GregorioGlyphType.PUNCTA_INCLINATA:
      case GregorioGlyphType._2_PUNCTA_INCLINATA_DESCENDENS:
      case GregorioGlyphType._3_PUNCTA_INCLINATA_DESCENDENS:
      case GregorioGlyphType._4_PUNCTA_INCLINATA_DESCENDENS:
      case GregorioGlyphType._5_PUNCTA_INCLINATA_DESCENDENS:
      case GregorioGlyphType.TORCULUS:
        if (!next_note) {
          special_punctum = 1;
        }
        break;
      default:
        break;
    }
    switch (current_note.shape) {
      case GregorioShape.PUNCTUM_INCLINATUM_ASCENDENS:
      case GregorioShape.PUNCTUM_INCLINATUM_STANS:
      case GregorioShape.PUNCTUM_INCLINATUM_DESCENDENS:
      case GregorioShape.PUNCTUM_INCLINATUM_DEMINUTUS:
        punctum_inclinatum = 1;
        break;
      default:
        break;
    }
    /* when the punctum mora is on a note on a line, and the prior note is on
    * the space immediately above, the dot is placed on the space below the
    * line instead */
    // eslint-disable-next-line max-len
    if (previous_note && (previous_note.pitch - current_note.pitch == 1) && is_on_a_line(current_note.pitch) && (previous_note.signs == GregorioSign.PUNCTUM_MORA || previous_note.signs == GregorioSign.V_EPISEMA_PUNCTUM_MORA || previous_note.choral_sign)) {
      special_punctum = 1;
    }

    if (current_note.mora_vposition) {
      special_punctum = current_note.mora_vposition == GregorioVPosition.BELOW ? 1 : 0;
    }

    if (shift_before) {
      if (next_note!.pitch - current_note.pitch == -1 || next_note!.pitch - current_note.pitch == 1) {
        // fprintf(f, "\\GrePunctumMora{%d}{3}{%d}{%d}%%\n",
        //   pitch, special_punctum, punctum_inclinatum);
        return new Mora(context, pitch, 3, special_punctum, punctum_inclinatum);
      } else {
        // fprintf(f, "\\GrePunctumMora{%d}{2}{%d}{%d}%%\n",
        //   pitch, special_punctum, punctum_inclinatum);
        return new Mora(context, pitch, 2, special_punctum, punctum_inclinatum);
      }

    }
    /* There are two special cases. The first: if the next glyph is a
    * ZERO_WIDTH_SPACE, and the current glyph is a PES, and the punctum mora
    * is on the first note, and the first note of the next glyph is at least
    * two (or three depending on something) pitches higher than the current
    * note.
    * You'll all have understood, this case is quite rare... but when it
    * appears, we pass 1 as a second argument of \punctummora so that it
    * removes the space introduced by the punctummora. */
    if (glyph.notes.glyph_type === GregorioGlyphType.PODATUS && next_glyph
      && next_glyph.type === GregorioType.SPACE
      && next_glyph.misc.info.space === GregorioSpace.ZERO_WIDTH
      && next_note && next_next_glyph
      && next_next_glyph.type === GregorioType.GLYPH
      && next_next_glyph.notes.first_note
      && (next_next_glyph.notes.first_note.pitch - current_note.pitch > 1)) {
      // fprintf(f, "\\GrePunctumMora{%d}{1}{%d}{%d}%%\n", pitch_value(pitch),
      //   special_punctum, punctum_inclinatum);
      return new Mora(context, pitch, 1, special_punctum, punctum_inclinatum);
    }
    /* if there is a punctum or a auctum dumplex on a note after, we put a
    * zero-width punctum */
    let current_note_index_tmp = current_note_index + 1;
    let tmpnote = glyph.notes.notes[current_note_index_tmp];
    let no_space = 0;
    while (tmpnote) {
      if (tmpnote.signs == GregorioSign.PUNCTUM_MORA || tmpnote.signs == GregorioSign.AUCTUM_DUPLEX
        || tmpnote.signs == GregorioSign.V_EPISEMA_PUNCTUM_MORA
        || tmpnote.signs == GregorioSign.V_EPISEMA_AUCTUM_DUPLEX
        || tmpnote.choral_sign) {
        no_space = 1;
        break;
      }
      current_note_index_tmp++;
      tmpnote = glyph.notes.notes[current_note_index_tmp];
    }
    /* use a special no-space punctum mora for ascending fusion */
    if (fuse_to_next_note) {
      no_space = 1;
      if (fuse_to_next_note > 0) {
        special_punctum = 1;
      }
    }

    // eslint-disable-next-line max-len
    if (next_element?.type === GregorioType.SPACE && next_element?.misc?.info?.space === GregorioSpace.INTERGLYPH_SPACE) {
      no_space = 1;
    }

    return new Mora(context, pitch, no_space, special_punctum, punctum_inclinatum);
  }

  public static writeAuctumDuplex(context: ChantContext,
    current_note: GregorioNoteElement, prev_note?: GregorioNoteElement) {

    let pitch = current_note.pitch;
    let previous_pitch = 0;
    let second_pitch = 0;
    let special_punctum = 0;

    if (prev_note) {
      if (prev_note.pitch - current_note.pitch === -1 || prev_note.pitch - current_note.pitch === 1) {
        special_punctum = 1;
      }
      second_pitch = prev_note.pitch;
      previous_pitch = second_pitch;
    }

    if (!previous_pitch || previous_pitch === pitch) {
      if (is_on_a_line(pitch)) {
        second_pitch = pitch;
        special_punctum = 1;
      } else {
        second_pitch = pitch + 1;
      }
    }

    if (pitch > second_pitch) {
      previous_pitch = pitch;
      pitch = second_pitch;
      second_pitch = previous_pitch;
    }

    return [
      new Mora(context, pitch, 1, special_punctum, 0),
      new Mora(context, second_pitch, 0, 0, 0),
    ];
  }
}
